import { DataType } from '../types/secs';
import { getAllMessages } from '../utils/secsDataLoader';
import { resolveMessageDataItem } from '../utils/dataItems';
import { useNavigate } from 'react-router-dom';

interface DataTypeCardProps {
  code: string;
  dataType: DataType;
}

export default function DataTypeCard({ code, dataType }: DataTypeCardProps) {
  const navigate = useNavigate();

  // Find all messages that use this data type
  const messagesUsingType = getAllMessages().filter(msg =>
    msg.dataItems.some(item => resolveMessageDataItem(item).type === code)
  );

  const getTypeCategory = (code: string): string => {
    if (code === 'L') return 'Structure';
    if (code === 'A') return 'Text';
    if (code === 'B' || code === 'BOOLEAN') return 'Binary';
    if (code.startsWith('U')) return 'Unsigned Integer';
    if (code.startsWith('I')) return 'Signed Integer';
    if (code.startsWith('F')) return 'Floating Point';
    return 'Other';
  };

  const getCategoryColor = (category: string): string => {
    switch (category) {
      case 'Structure': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'Text': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'Binary': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      case 'Unsigned Integer': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'Signed Integer': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'Floating Point': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const category = getTypeCategory(code);

  return (
    <div className="card p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-2xl font-bold font-mono text-primary-600 dark:text-primary-400">
              {code}
            </span>
            <span className={`badge ${getCategoryColor(category)}`}>
              {category}
            </span>
          </div>
          <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">
            {dataType.name}
          </h3>
        </div>
        <div className="text-right">
          <div className="text-sm text-[var(--color-text-tertiary)]">Size</div>
          <div className="text-lg font-semibold font-mono text-[var(--color-text-primary)]">
            {typeof dataType.byteSize === 'number' ? `${dataType.byteSize} byte${dataType.byteSize > 1 ? 's' : ''}` : dataType.byteSize}
          </div>
        </div>
      </div>

      <p className="text-[var(--color-text-secondary)] text-sm mb-4 leading-relaxed">
        {dataType.description}
      </p>

      {messagesUsingType.length > 0 && (
        <div className="pt-4 border-t border-[var(--color-border)]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-[var(--color-text-tertiary)] uppercase">
              Used in {messagesUsingType.length} message{messagesUsingType.length > 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex flex-wrap gap-1">
            {messagesUsingType.slice(0, 6).map((msg) => {
              const messageId = `S${msg.stream}F${msg.function}`;
              return (
                <button
                  key={messageId}
                  onClick={() => navigate(`/message/${messageId}`)}
                  className="text-xs px-2 py-1 bg-[var(--color-bg-tertiary)] hover:bg-primary-100 dark:hover:bg-primary-900 rounded font-mono transition-colors"
                  title={msg.name}
                >
                  {messageId}
                </button>
              );
            })}
            {messagesUsingType.length > 6 && (
              <span className="text-xs px-2 py-1 text-[var(--color-text-tertiary)]">
                +{messagesUsingType.length - 6} more
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
